// Trigger a browser download for a same-origin API response. The api-client
// (openapi-fetch) parses JSON automatically, which is the wrong shape for
// `text/csv` (and any other binary/text-stream content) — using raw fetch
// here keeps the response body verbatim.
//
// Dev-bypass parity: this fetch reads the `dev_user_id` cookie and promotes
// it to `X-Dev-User-Id` the same way `apps/web/src/lib/api-client.ts` does.
// In prod the user's Clerk cookie is sent automatically by the browser; in
// dev we have to forward the bypass header explicitly because the cookie
// itself doesn't reach the server through the Vite proxy.

const DEV_BYPASS_COOKIE = 'dev_user_id';
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function readDevBypassUserId(): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${DEV_BYPASS_COOKIE}=([^;]+)`),
  );
  if (!m || m[1] === undefined) return null;
  const value = decodeURIComponent(m[1]);
  return UUID_RE.test(value) ? value : null;
}

// Anchor-create/click/remove dance shared by every blob download on the site
// (API file downloads here, and the .ics "Add to calendar" download in
// `components/events/ics.ts`). No-ops outside the browser so it's safe to
// call from code that also runs in SSR/test contexts.
export function downloadBlob(filename: string, blob: Blob): void {
  if (typeof window === 'undefined') return;
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Defer revoke so Safari has time to start the download.
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1_000);
}

export async function downloadFile(url: string, filename: string): Promise<void> {
  const headers = new Headers();
  const userId = readDevBypassUserId();
  if (userId !== null) headers.set('X-Dev-User-Id', userId);

  const response = await fetch(url, { headers });
  if (!response.ok) {
    throw new Error(`Download failed (${response.status}).`);
  }

  const blob = await response.blob();
  downloadBlob(filename, blob);
}

// UTF-8 BOM: without it, Excel mis-renders non-ASCII characters (accented
// names, etc.) in a CSV opened directly rather than imported.
const UTF8_BOM = '﻿';

export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([UTF8_BOM, csv], { type: 'text/csv;charset=utf-8' });
  downloadBlob(filename, blob);
}
